import RichText from "@/components/RichText";
import SmartImage from "@/components/SmartImage";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";
import CtaButtonBlocks, { ctaButtonBlocks } from "./CtaButtonBlocks";

// Modèle de bandeau principal « image plein écran », texte en surimpression.
// Pour les activités qui se MONTRENT : restaurant, artisan, hôtel, salon.
// Le texte est toujours clair, donc l'image est toujours voilée — le degré
// du voile est réglable (`overlay_opacity`) mais jamais nul par défaut : une
// accroche blanche sur une photo claire est illisible.
const DEFAULTS = {
  eyebrow: "",
  headline: "Votre accroche principale",
  subheadline: "",
  cta_primary_label: "Nous contacter",
  cta_primary_url: "/#contact",
  cta_secondary_label: "",
  cta_secondary_url: "",
  image_url: "",
  overlay_opacity: "55",
  text_align: "center",
  anchor_id: "",
};

const ALIGNMENTS = {
  left: { block: "items-start text-left", row: "justify-start" },
  center: { block: "items-center text-center", row: "justify-center" },
  right: { block: "items-end text-right", row: "justify-end" },
};

export default function HeroImageSection({ props = {}, blocks }) {
  const t = { ...DEFAULTS, ...props };
  const primary = resolveButtonStyle(t, "cta_primary");
  const secondary = resolveButtonStyle(t, "cta_secondary");
  const extraButtons = ctaButtonBlocks(blocks);
  const align = ALIGNMENTS[t.text_align] || ALIGNMENTS.center;
  // Le champ est un pourcentage saisi dans le Studio ; une valeur illisible
  // retombe sur le défaut plutôt que de produire un voile opaque ou nul.
  const overlay = Number.isFinite(Number(t.overlay_opacity)) ? Number(t.overlay_opacity) / 100 : 0.55;

  return (
    <section id={t.anchor_id || undefined} className="relative isolate overflow-hidden bg-ink scroll-mt-28">
      {t.image_url && (
        <SmartImage
          src={t.image_url}
          alt=""
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-ink" style={{ opacity: overlay }} />

      <div className={`relative max-w-[1240px] mx-auto px-6 md:px-10 py-20 md:py-28 flex flex-col ${align.block}`}>
        {t.eyebrow && (
          <RichText as="p" className="text-[13px] font-semibold tracking-wide text-gold uppercase mb-4" html={t.eyebrow} />
        )}
        <RichText
          as="h1"
          className="font-display font-semibold text-[36px] md:text-[52px] leading-[1.05] tracking-[-0.025em] text-white max-w-3xl"
          html={t.headline}
        />
        {t.subheadline && (
          <RichText
            as="p"
            preserveParagraphs
            className="text-[17px] leading-relaxed text-white/80 mt-5 max-w-xl"
            html={t.subheadline}
          />
        )}
        {(t.cta_primary_label || t.cta_secondary_label || extraButtons.length > 0) && (
          <div className={`flex flex-wrap items-center gap-3 mt-9 ${align.row}`}>
            {t.cta_primary_label && (
              <a
                href={t.cta_primary_url || "#"}
                className={`inline-flex items-center gap-2 bg-white text-ink px-8 py-3.5 text-[14px] font-semibold hover:opacity-90 transition-opacity ${primary.className}`}
                style={primary.style}
                aria-label={!primary.showText ? t.cta_primary_label : undefined}
              >
                {primary.showIcon && primary.Icon && <primary.Icon className="h-4 w-4" />}
                {primary.showText && t.cta_primary_label}
              </a>
            )}
            {t.cta_secondary_label && (
              <a
                href={t.cta_secondary_url || "#"}
                className={`inline-flex items-center gap-2 border-control border-white/30 text-white px-8 py-3.5 text-[14px] font-semibold hover:bg-white/10 transition-colors ${secondary.className}`}
                style={secondary.style}
                aria-label={!secondary.showText ? t.cta_secondary_label : undefined}
              >
                {secondary.showIcon && secondary.Icon && <secondary.Icon className="h-4 w-4" />}
                {secondary.showText && t.cta_secondary_label}
              </a>
            )}
            <CtaButtonBlocks blocks={extraButtons} tone="dark" />
          </div>
        )}
      </div>
    </section>
  );
}
